import {
  Action,
  ActionGroup,
  Autocomplete,
  Button,
  Content,
  FieldDescription,
  FieldError,
  Header,
  Heading,
  Icon,
  Modal,
  Option,
  Section,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Text,
  TextField,
} from "@mittwald/flow-remote-react-components";
import { IconTrash } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import {
  ALL_MIME_TYPES,
  isKnownMimeType,
  MIME_FORMAT,
  type GzipConfig,
} from "../pageSpeedConfig.ts";

interface DraftRule {
  id: string;
  mimeType: string;
}

let nextId = 0;
const makeId = (): string => `g${String(++nextId)}`;

const toDraft = (config: GzipConfig): DraftRule[] =>
  config.customMimes.map((mimeType) => ({ id: makeId(), mimeType }));

const renderMimeSuggestions = (query: string) =>
  query.length >= 2
    ? ALL_MIME_TYPES.filter((m) =>
        m.toLowerCase().includes(query.toLowerCase()),
      )
        .slice(0, 50)
        .map((m) => (
          <Option key={m} value={m} textValue={m}>
            {m}
          </Option>
        ))
    : null;

interface PageSpeedGzipDialogProps {
  initial: GzipConfig;
  onSave: (next: GzipConfig) => void;
}

export const PageSpeedGzipDialog = ({
  initial,
  onSave,
}: PageSpeedGzipDialogProps) => {
  const [rules, setRules] = useState<DraftRule[]>(() => toDraft(initial));
  const [autoFocusId, setAutoFocusId] = useState<string | null>(null);

  useEffect(() => {
    if (autoFocusId !== null) {
      const handle = requestAnimationFrame(() => {
        setAutoFocusId(null);
      });
      return () => {
        cancelAnimationFrame(handle);
      };
    }
  }, [autoFocusId]);

  const addRule = () => {
    const newId = makeId();
    setRules((r) => [...r, { id: newId, mimeType: "" }]);
    setAutoFocusId(newId);
  };

  const updateRule = (id: string, mimeType: string) => {
    setRules((r) =>
      r.map((rule) => (rule.id === id ? { ...rule, mimeType } : rule)),
    );
  };

  const removeRule = (id: string) => {
    setRules((r) => r.filter((rule) => rule.id !== id));
  };

  const resetRules = () => {
    setRules([]);
  };

  const canSave = rules.every((r) => MIME_FORMAT.test(r.mimeType));

  return (
    <Modal offCanvas>
      <Heading>Komprimierung anpassen</Heading>
      <Content>
        <Section>
          <Header>
            <Heading>Eigene MIME-Types</Heading>
            {rules.length > 0 && (
              <Button
                onPress={resetRules}
                variant="soft"
                color="secondary"
                size="s"
              >
                Zurücksetzen
              </Button>
            )}
            <Button onPress={addRule} color="success" size="s">
              Regel hinzufügen
            </Button>
          </Header>
          <Text>
            Standardmäßig werden HTML, CSS, JavaScript, JSON, XML, SVG und
            Schriften komprimiert. Hier kannst du weitere MIME-Types ergänzen.
          </Text>
          {rules.length > 0 && (
            <Table aria-label="Eigene GZIP-Regeln">
              <TableHeader>
                <TableColumn>MIME-Type</TableColumn>
                <TableColumn>Aktion</TableColumn>
              </TableHeader>
              <TableBody>
                {rules.map((rule) => (
                  <TableRow key={rule.id}>
                    <TableCell>
                      <Autocomplete>
                        <TextField
                          autoFocus={rule.id === autoFocusId}
                          aria-label="MIME-Type"
                          value={rule.mimeType}
                          onChange={(v) => {
                            updateRule(rule.id, v);
                          }}
                          isInvalid={
                            rule.mimeType.length > 0 &&
                            !MIME_FORMAT.test(rule.mimeType)
                          }
                        >
                          <FieldError>
                            Ungültiges Format. Erwartet: type/subtype
                          </FieldError>
                          {rule.mimeType.length > 0 &&
                            MIME_FORMAT.test(rule.mimeType) &&
                            !isKnownMimeType(rule.mimeType) && (
                              <FieldDescription>
                                Nicht in der Liste gängiger MIME-Types.
                                Tippfehler?
                              </FieldDescription>
                            )}
                        </TextField>
                        {renderMimeSuggestions(rule.mimeType)}
                      </Autocomplete>
                    </TableCell>
                    <TableCell>
                      <Button
                        aria-label="Regel löschen"
                        color="danger"
                        variant="plain"
                        onPress={() => {
                          removeRule(rule.id);
                        }}
                      >
                        <Icon>
                          <IconTrash />
                        </Icon>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Section>
      </Content>

      <ActionGroup>
        <Action closeOverlay="Modal">
          <Action
            onAction={() => {
              onSave({
                enabled: true,
                customMimes: rules.map((r) => r.mimeType),
              });
            }}
          >
            <Button color="success" isDisabled={!canSave}>
              Speichern
            </Button>
          </Action>
          <Button color="secondary" variant="soft">
            Abbrechen
          </Button>
        </Action>
      </ActionGroup>
    </Modal>
  );
};
